import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Building2, Save } from 'lucide-react';
import { useNetContratantePerfil, useUpsertContratantePerfil, type ContratanteContatos } from '@/modules/financiamento/hooks/useNetContratantePerfil';

interface ContratanteInlineFormProps {
  contratoId: string;
  /** Pre-filled contratante name from emp_contratos */
  contratanteNome?: string;
  canEdit: boolean;
}

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const isValidCnpjFormat = (cnpj: string) => {
  const digits = cnpj.replace(/\D/g, '');
  return digits.length === 14;
};

export function ContratanteInlineForm({ contratoId, contratanteNome, canEdit }: ContratanteInlineFormProps) {
  const { data: perfil, isLoading } = useNetContratantePerfil(contratoId);
  const upsertMutation = useUpsertContratantePerfil();

  const [isOpen, setIsOpen] = useState(false);
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [email, setEmail] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [telefone, setTelefone] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (perfil) {
      setNomeEmpresa(perfil.nome_empresa || '');
      setCnpj(perfil.cnpj ? formatCnpj(perfil.cnpj) : '');
      setEmail((perfil.contatos as ContratanteContatos)?.email || (perfil.contatos as ContratanteContatos)?.email_principal || '');
      setResponsavel((perfil.contatos as ContratanteContatos)?.responsavel || '');
      setTelefone((perfil.contatos as ContratanteContatos)?.telefone || '');
      setDirty(false);
    } else if (contratanteNome) {
      setNomeEmpresa(contratanteNome);
    }
  }, [perfil, contratanteNome]);

  const handleSave = () => {
    const rawCnpj = cnpj.replace(/\D/g, '');
    if (!nomeEmpresa.trim() || !rawCnpj) return;

    upsertMutation.mutate({
      contrato_id: contratoId,
      nome_empresa: nomeEmpresa.trim(),
      cnpj: rawCnpj,
      contatos: {
        email: email.trim() || undefined,
        responsavel: responsavel.trim() || undefined,
        telefone: telefone.trim() || undefined,
      },
    }, {
      onSuccess: () => setDirty(false),
    });
  };

  const handleChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setDirty(true);
  };

  if (isLoading) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left p-3 rounded-md border border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <Building2 className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-primary flex-1">
            Dados do Contratante (Aprovador)
          </span>
          {perfil && (
            <span className="text-xs text-muted-foreground mr-2">
              {perfil.nome_empresa}
            </span>
          )}
          {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-2 space-y-3 p-3 border rounded-md bg-muted/20">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome da Empresa *</Label>
            <Input
              value={nomeEmpresa}
              onChange={handleChange(setNomeEmpresa)}
              placeholder="Razão Social"
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">CNPJ *</Label>
            <Input
              value={cnpj}
              onChange={(e) => {
                setCnpj(formatCnpj(e.target.value));
                setDirty(true);
              }}
              placeholder="00.000.000/0000-00"
              disabled={!canEdit}
              className={cnpj && !isValidCnpjFormat(cnpj) ? 'border-destructive' : ''}
            />
            {cnpj && !isValidCnpjFormat(cnpj) && (
              <p className="text-[10px] text-destructive">CNPJ deve ter 14 dígitos</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">E-mail</Label>
            <Input
              type="email"
              value={email}
              onChange={handleChange(setEmail)}
              placeholder="email@empresa.com"
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Responsável</Label>
            <Input
              value={responsavel}
              onChange={handleChange(setResponsavel)}
              placeholder="Nome do responsável"
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Telefone</Label>
            <Input
              value={telefone}
              onChange={handleChange(setTelefone)}
              placeholder="+55 (00) 00000-0000"
              disabled={!canEdit}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || !nomeEmpresa.trim() || !cnpj || !isValidCnpjFormat(cnpj) || upsertMutation.isPending}
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {upsertMutation.isPending ? 'Salvando...' : 'Salvar Contratante'}
            </Button>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          Estes dados serão usados como padrão no envio de aprovação por e-mail.
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}
